import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ArrowRight, ChevronDown, Trophy } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Card } from '@/components/ui'
import {
  METRIC_META,
  barTone,
  formatRatingValue,
  placeTone,
  rowAccentTone,
  type BoardRow,
  type MetricKey,
  type ScopeKey,
  type UnifiedRatingsData,
  type UnitKey
} from '@/lib/ratings/unifiedRatings'
import { useRatingsFiltersStore } from '@/store'

interface RatingsBoardProps {
  data: UnifiedRatingsData
  /** explorer — УК выбирает любую землю/размер/франчайзи; self — фикс. «моё» (партнёрская страница). */
  mode?: 'explorer' | 'self'
  highlight?: { studioId?: number; partnerNumber?: number }
  /** Для mode='self': ключи земли/размера/франчайзи текущей студии. */
  selfKeys?: { land?: string; size?: string; partner?: string }
  topN?: number
  compact?: boolean
  sideHeader?: ReactNode
  rightHeaderAction?: ReactNode
  hiddenMetricKeys?: MetricKey[]
  metricDetailLinks?: Partial<Record<MetricKey, string>>
}

const UNIT_OPTIONS: Array<{ value: UnitKey; label: string }> = [
  { value: 'studio', label: 'Студии' },
  { value: 'franchisee', label: 'Франчайзи' }
]

const SCOPE_OPTIONS: Array<{ value: ScopeKey; label: string }> = [
  { value: 'network', label: 'Вся сеть' },
  { value: 'land', label: 'Земля' },
  { value: 'size', label: 'Размер города' },
  { value: 'franchisee', label: 'Франчайзи' }
]

const SALES_METRIC_KEYS: MetricKey[] = ['sales_conversion', 'average_ticket_new_sales']
const SUBSCRIPTION_METRIC_KEYS: MetricKey[] = ['active_subscriptions', 'new_subscriptions']
const MARKETING_METRIC_KEYS: MetricKey[] = ['leads_per_studio', 'cost_per_lead', 'romi']
const TRAINING_METRIC_KEYS: MetricKey[] = ['visits_per_studio', 'training_quality', 'classic_group_index']
const GROUPED_METRIC_KEYS = new Set<MetricKey>([
  ...SALES_METRIC_KEYS,
  ...SUBSCRIPTION_METRIC_KEYS,
  ...MARKETING_METRIC_KEYS,
  ...TRAINING_METRIC_KEYS,
])

function getPlaceRangeEnd(row: Pick<BoardRow, 'place' | 'place_label'>): number {
  const match = row.place_label.match(/^(\d+)-(\d+)$/)
  return match ? Number(match[2]) : row.place
}

function buildVisibleRows(
  rows: BoardRow[],
  currentId: string | null,
  topPlaces: number,
  neighborRadius: number,
  mode: 'explorer' | 'self'
): Array<BoardRow & { isGap?: boolean }> {
  if (rows.length === 0) return []

  if (mode !== 'self' || !currentId) {
    return rows.slice(0, topPlaces)
  }

  const current = rows.find((row) => row.id === currentId)
  if (!current) return rows.slice(0, topPlaces)

  const visible = new Map<string, BoardRow>()
  const neighborStart = Math.max(1, current.place - neighborRadius)
  const neighborEnd = current.place + neighborRadius

  for (const row of rows) {
    if (row.place <= topPlaces) {
      visible.set(row.id, row)
      continue
    }
    if (row.place >= neighborStart && row.place <= neighborEnd) {
      visible.set(row.id, row)
    }
  }

  const sortedRows = Array.from(visible.values()).sort((a, b) => {
    if (a.place !== b.place) return a.place - b.place
    return a.id.localeCompare(b.id, 'ru', { numeric: true })
  })

  return sortedRows.reduce<Array<BoardRow & { isGap?: boolean }>>((acc, row, index) => {
    const previous = acc[acc.length - 1]
    if (index > 0 && previous && !previous.isGap) {
      const previousEnd = getPlaceRangeEnd(previous)
      if (row.place > previousEnd + 1) {
        acc.push({ ...row, isGap: true })
      }
    }
    acc.push(row)
    return acc
  }, [])
}

function buildScopeSummary(params: {
  unit: UnitKey
  scope: ScopeKey
  landKey: string
  sizeKey: string
  partnerKey: string
  partnerLabel?: string
}): string {
  const unitLabel = params.unit === 'franchisee' ? 'франчайзи' : 'студий'

  if (params.scope === 'network') {
    return params.unit === 'franchisee' ? 'Среди всех франчайзи сети' : 'Среди всех студий сети'
  }
  if (params.scope === 'land') {
    return `Среди ${unitLabel} земли «${params.landKey || 'Без земли'}»`
  }
  if (params.scope === 'size') {
    return `Среди ${unitLabel} группы «${params.sizeKey || 'Без группы'}»`
  }
  return `Среди студий франчайзи «${params.partnerLabel || params.partnerKey || 'Не указан'}»`
}

export function RatingsBoard({ data, mode = 'explorer', highlight, selfKeys, topN = 20, compact = false, sideHeader, rightHeaderAction, hiddenMetricKeys = [], metricDetailLinks }: RatingsBoardProps) {
  const visibleMetricSet = useMemo(() => new Set(hiddenMetricKeys), [hiddenMetricKeys])
  const generalMetricKeys = useMemo(
    () => METRIC_META.map((metric) => metric.key).filter((key): key is MetricKey => !GROUPED_METRIC_KEYS.has(key) && !visibleMetricSet.has(key)),
    [visibleMetricSet]
  )
  const salesMetricKeys = useMemo(
    () => SALES_METRIC_KEYS.filter((key) => !visibleMetricSet.has(key)),
    [visibleMetricSet]
  )
  const subscriptionMetricKeys = useMemo(
    () => SUBSCRIPTION_METRIC_KEYS.filter((key) => !visibleMetricSet.has(key)),
    [visibleMetricSet]
  )
  const marketingMetricKeys = useMemo(
    () => MARKETING_METRIC_KEYS.filter((key) => !visibleMetricSet.has(key)),
    [visibleMetricSet]
  )
  const trainingMetricKeys = useMemo(
    () => TRAINING_METRIC_KEYS.filter((key) => !visibleMetricSet.has(key)),
    [visibleMetricSet]
  )
  const [localMetric, setLocalMetric] = useState<MetricKey>('revenue')
  const [localUnit, setLocalUnit] = useState<UnitKey>('studio')
  const [localScope, setLocalScope] = useState<ScopeKey>('network')
  const storedMetric = useRatingsFiltersStore((state) => state.metric)
  const storedUnit = useRatingsFiltersStore((state) => state.unit)
  const storedScope = useRatingsFiltersStore((state) => state.scope)
  const setStoredMetric = useRatingsFiltersStore((state) => state.setMetric)
  const setStoredUnit = useRatingsFiltersStore((state) => state.setUnit)
  const setStoredScope = useRatingsFiltersStore((state) => state.setScope)
  const metric = mode === 'self' ? storedMetric : localMetric
  const unit = mode === 'self' ? storedUnit : localUnit
  const scope = mode === 'self' ? storedScope : localScope
  const setMetric = useCallback(
    (value: MetricKey) => (mode === 'self' ? setStoredMetric(value) : setLocalMetric(value)),
    [mode, setStoredMetric]
  )
  const setUnit = useCallback(
    (value: UnitKey) => (mode === 'self' ? setStoredUnit(value) : setLocalUnit(value)),
    [mode, setStoredUnit]
  )
  const setScope = useCallback(
    (value: ScopeKey) => (mode === 'self' ? setStoredScope(value) : setLocalScope(value)),
    [mode, setStoredScope]
  )
  const [landKey, setLandKey] = useState<string>('')
  const [sizeKey, setSizeKey] = useState<string>('')
  const [partnerKey, setPartnerKey] = useState<string>('')
  const [salesExpanded, setSalesExpanded] = useState(false)
  const [subscriptionExpanded, setSubscriptionExpanded] = useState(false)
  const [marketingExpanded, setMarketingExpanded] = useState(false)
  const [trainingExpanded, setTrainingExpanded] = useState(false)

  const dataset = data[metric]
  const metricDetailLink = metricDetailLinks?.[metric]
  const scoped = dataset.studio
  const franchiseeScoped = dataset.franchisee

  useEffect(() => {
    if (!visibleMetricSet.has(metric)) return
    const fallbackMetric = generalMetricKeys[0] || salesMetricKeys[0] || subscriptionMetricKeys[0] || marketingMetricKeys[0] || trainingMetricKeys[0] || 'revenue'
    setMetric(fallbackMetric)
  }, [generalMetricKeys, marketingMetricKeys, metric, salesMetricKeys, setMetric, subscriptionMetricKeys, trainingMetricKeys, visibleMetricSet])

  useEffect(() => {
    if (SALES_METRIC_KEYS.includes(metric)) setSalesExpanded(true)
    if (SUBSCRIPTION_METRIC_KEYS.includes(metric)) setSubscriptionExpanded(true)
    if (MARKETING_METRIC_KEYS.includes(metric)) setMarketingExpanded(true)
    if (TRAINING_METRIC_KEYS.includes(metric)) setTrainingExpanded(true)
  }, [metric])

  const scopeOptions = unit === 'franchisee'
    ? SCOPE_OPTIONS.filter((option) => option.value === 'network' || option.value === 'land')
    : SCOPE_OPTIONS
  const effectiveScope: ScopeKey = scopeOptions.some((option) => option.value === scope) ? scope : 'network'

  // Держим выбранные ключи валидными при смене метрики/данных.
  useEffect(() => {
    if (mode === 'self') {
      if (selfKeys?.land) setLandKey(selfKeys.land)
      if (selfKeys?.size) setSizeKey(selfKeys.size)
      if (selfKeys?.partner) setPartnerKey(selfKeys.partner)
      return
    }
    const landSource = unit === 'franchisee' ? franchiseeScoped : scoped
    setLandKey((prev) => (prev && landSource.landByKey[prev] ? prev : landSource.landOptions[0]?.value || ''))
    setSizeKey((prev) => (prev && scoped.sizeByKey[prev] ? prev : scoped.sizeOptions[0]?.value || ''))
    setPartnerKey((prev) => (prev && scoped.partnerByKey[prev] ? prev : scoped.partnerOptions[0]?.value || ''))
  }, [franchiseeScoped, mode, scoped, selfKeys, unit])

  const rows: BoardRow[] = useMemo(() => {
    if (unit === 'franchisee') {
      if (effectiveScope === 'land') return dataset.franchisee.landByKey[landKey] || []
      return dataset.franchisee.network
    }
    if (effectiveScope === 'land') return scoped.landByKey[landKey] || []
    if (effectiveScope === 'size') return scoped.sizeByKey[sizeKey] || []
    if (effectiveScope === 'franchisee') return scoped.partnerByKey[partnerKey] || []
    return scoped.network
  }, [unit, effectiveScope, dataset, scoped, landKey, sizeKey, partnerKey])

  const currentId = unit === 'franchisee'
    ? (highlight?.partnerNumber != null ? `partner-${highlight.partnerNumber}` : null)
    : (highlight?.studioId != null ? `studio-${highlight.studioId}` : null)

  // Топ-N + при необходимости подсветка текущей строки за пределами топа.
  const visibleRows: Array<BoardRow & { isGap?: boolean }> = useMemo(
    () => buildVisibleRows(rows, currentId, topN, 5, mode),
    [rows, currentId, topN, mode]
  )

  const isFranchisee = unit === 'franchisee'
  const hasDetail = Boolean(dataset.detailColumnLabel)
  const valueHeader = dataset.label
  const activePartnerLabel = useMemo(
    () => scoped.partnerOptions.find((option) => option.value === partnerKey)?.label,
    [partnerKey, scoped.partnerOptions]
  )
  const scopeSummary = useMemo(
    () => buildScopeSummary({
      unit,
      scope: effectiveScope,
      landKey,
      sizeKey,
      partnerKey,
      partnerLabel: activePartnerLabel,
    }),
    [unit, effectiveScope, landKey, sizeKey, partnerKey, activePartnerLabel]
  )

  // Максимум для пропорциональных баров (строки отсортированы по убыванию; берём положительный максимум).
  const maxValue = useMemo(
    () => visibleRows.reduce((max, row) => (row.metric_value > max ? row.metric_value : max), 0),
    [visibleRows]
  )
  const panelClass = compact
    ? 'space-y-3 rounded-lg border border-border/60 bg-card p-2 shadow-sm lg:sticky lg:top-2'
    : 'space-y-5 rounded-2xl border border-border/60 bg-card p-4 shadow-sm lg:sticky lg:top-4'
  const sectionGapClass = compact ? 'space-y-1.5' : 'space-y-2'
  const metricGridClass = compact ? 'grid grid-cols-2 gap-1 lg:grid-cols-1' : 'grid grid-cols-2 gap-1.5 lg:grid-cols-1'
  const filterButtonClass = compact ? 'rounded-lg px-2 py-1.5 text-left text-xs font-medium transition-all' : 'rounded-xl px-3 py-2 text-left text-sm font-medium transition-all'
  const segmentButtonClass = compact ? 'flex-1 rounded-md px-2 py-1 text-xs font-medium transition-all' : 'flex-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-all'
  const selectClass = compact
    ? 'mt-1 w-full rounded-lg border border-border bg-background px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary/40'
    : 'mt-1.5 w-full rounded-xl border border-border bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40'
  const cardClass = compact ? 'min-h-0 space-y-1.5 px-2 py-2' : 'space-y-3 py-3'
  const tableOuterClass = compact ? 'overflow-hidden rounded-lg border border-border/60 bg-card shadow-sm' : 'overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm'
  const thClass = compact
    ? 'px-2 py-1.5 text-left text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/80'
    : 'px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/80'
  const tdClass = compact ? 'px-2 py-1' : 'px-3 py-1.5'
  /*
   * ОДНО «выбрано» на весь экран.
   *
   * Было четыре разных способа сказать одно и то же: насыщенный градиент
   * `from-amber-400 to-amber-500` у метрики, бледный `from-amber-100
   * to-orange-50` у группы и `bg-primary/15 text-amber-900` у скоупа и
   * бейджа. Громкость при этом не совпадала с важностью — самый частый
   * клик (метрика) кричал, равный ему по смыслу выбор скоупа шептал.
   *
   * Плюс два измеримых дефекта, которые лечатся тем же:
   *  • `text-amber-950` на `to-amber-500` в тёмной теме давало 2,07:1
   *    (текст зеркалится в почти-белый, а оттенок 500 самозеркальный,
   *    то есть фон остаётся ярко-жёлтым — см. scripts/check-theme-solid-fills.mjs);
   *  • `text-secondary` — это роль ЗАЛИВКИ, а не текста: в тёмной теме
   *    она уходит в #525252 и на `bg-muted` даёт 1,81:1.
   *
   * Роли `bg-primary` / `text-primary-foreground` не зеркалятся вовсе,
   * поэтому пара верна в обеих темах по построению.
   */
  const SELECTED_CLASS = 'bg-primary text-primary-foreground shadow-sm'
  const UNSELECTED_CLASS = 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
  const groupButtonClass = (expanded: boolean, active: boolean) => `${filterButtonClass} flex w-full items-center justify-between ${
    active || expanded ? SELECTED_CLASS : UNSELECTED_CLASS
  }`

  return (
    <div className={`${compact ? 'flex h-full min-h-0 flex-col gap-2 lg:flex-row' : 'flex flex-col gap-4 lg:flex-row'}`}>
      {/* Левый рельс фильтров */}
      <aside className={compact ? 'lg:w-72 lg:flex-none' : 'lg:w-72 lg:flex-none'}>
        <div className={panelClass}>
          {sideHeader ? <div className="border-b border-border/60 pb-2">{sideHeader}</div> : null}
          <div className={sectionGapClass}>
            <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/70">Метрика</div>
            <div className={metricGridClass}>
              {METRIC_META.filter((m) => generalMetricKeys.includes(m.key)).map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setMetric(m.key)}
                  className={`${filterButtonClass} ${
                    metric === m.key
                      ? SELECTED_CLASS
                      : UNSELECTED_CLASS
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <div className={compact ? 'mt-1 space-y-1' : 'mt-1.5 space-y-1.5'}>
              {salesMetricKeys.length > 0 ? (
                <div>
                  <button
                    type="button"
                    onClick={() => setSalesExpanded((prev) => !prev)}
                    className={groupButtonClass(salesExpanded, SALES_METRIC_KEYS.includes(metric))}
                  >
                    <span className="block min-w-0 truncate">Рейтинги по продажам</span>
                    <ChevronDown className={`ml-3 h-4 w-4 flex-none transition-transform ${salesExpanded ? 'rotate-180' : ''}`} />
                  </button>
                  {salesExpanded ? (
                    <div className={compact ? 'mt-1 space-y-1 pl-2' : 'mt-1.5 space-y-1.5 pl-3'}>
                      {METRIC_META.filter((m) => salesMetricKeys.includes(m.key)).map((m) => (
                        <button
                          key={m.key}
                          type="button"
                          onClick={() => setMetric(m.key)}
                          className={`${filterButtonClass} w-full ${
                            metric === m.key
                              ? SELECTED_CLASS
                              : 'bg-muted/40 text-muted-foreground hover:bg-muted hover:text-foreground'
                          }`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
              {subscriptionMetricKeys.length > 0 ? (
                <div>
                  <button
                    type="button"
                    onClick={() => setSubscriptionExpanded((prev) => !prev)}
                    className={groupButtonClass(subscriptionExpanded, SUBSCRIPTION_METRIC_KEYS.includes(metric))}
                  >
                    <span className="block min-w-0 truncate">Рейтинги по подпискам</span>
                    <ChevronDown className={`ml-3 h-4 w-4 flex-none transition-transform ${subscriptionExpanded ? 'rotate-180' : ''}`} />
                  </button>
                  {subscriptionExpanded ? (
                    <div className={compact ? 'mt-1 space-y-1 pl-2' : 'mt-1.5 space-y-1.5 pl-3'}>
                      {METRIC_META.filter((m) => subscriptionMetricKeys.includes(m.key)).map((m) => (
                        <button
                          key={m.key}
                          type="button"
                          onClick={() => setMetric(m.key)}
                          className={`${filterButtonClass} w-full ${
                            metric === m.key
                              ? SELECTED_CLASS
                              : 'bg-muted/40 text-muted-foreground hover:bg-muted hover:text-foreground'
                          }`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
              {marketingMetricKeys.length > 0 ? (
                <div>
                  <button
                    type="button"
                    onClick={() => setMarketingExpanded((prev) => !prev)}
                    className={groupButtonClass(marketingExpanded, MARKETING_METRIC_KEYS.includes(metric))}
                  >
                    <span className="block min-w-0 truncate">Рейтинги по маркетингу</span>
                    <ChevronDown className={`ml-3 h-4 w-4 flex-none transition-transform ${marketingExpanded ? 'rotate-180' : ''}`} />
                  </button>
                  {marketingExpanded ? (
                    <div className={compact ? 'mt-1 space-y-1 pl-2' : 'mt-1.5 space-y-1.5 pl-3'}>
                      {METRIC_META.filter((m) => marketingMetricKeys.includes(m.key)).map((m) => (
                        <button
                          key={m.key}
                          type="button"
                          onClick={() => setMetric(m.key)}
                          className={`${filterButtonClass} w-full ${
                            metric === m.key
                              ? SELECTED_CLASS
                              : 'bg-muted/40 text-muted-foreground hover:bg-muted hover:text-foreground'
                          }`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className={compact ? 'mt-1 space-y-1' : 'mt-1.5 space-y-1.5'}>
              <button
                type="button"
                onClick={() => setTrainingExpanded((prev) => !prev)}
                className={groupButtonClass(trainingExpanded, TRAINING_METRIC_KEYS.includes(metric))}
              >
                <span className="block min-w-0 truncate">Рейтинги по тренировкам</span>
                <ChevronDown className={`ml-3 h-4 w-4 flex-none transition-transform ${trainingExpanded ? 'rotate-180' : ''}`} />
              </button>
              {trainingExpanded && trainingMetricKeys.length > 0 ? (
                <div className={compact ? 'space-y-1 pl-2' : 'space-y-1.5 pl-3'}>
                  {METRIC_META.filter((m) => trainingMetricKeys.includes(m.key)).map((m) => (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => setMetric(m.key)}
                      className={`${filterButtonClass} w-full ${
                        metric === m.key
                          ? SELECTED_CLASS
                          : 'bg-muted/40 text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <div className={sectionGapClass}>
            <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/70">Единица</div>
            <div className="inline-flex w-full rounded-lg bg-muted/60 p-1">
              {UNIT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setUnit(option.value)}
                  className={`${segmentButtonClass} ${
                    unit === option.value ? 'bg-card text-foreground shadow-sm ring-1 ring-border/60' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className={sectionGapClass}>
            <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/70">Среди кого</div>
            <div className={compact ? 'space-y-1' : 'space-y-1.5'}>
                {scopeOptions.map((option) => {
                  const active = scope === option.value
                  const needsKey = option.value === 'land' || option.value === 'size' || option.value === 'franchisee'
                  const keyValue = option.value === 'land' ? landKey : option.value === 'size' ? sizeKey : partnerKey
                  const keyOptions = option.value === 'land'
                    ? (unit === 'franchisee' ? franchiseeScoped.landOptions : scoped.landOptions)
                    : option.value === 'size'
                      ? scoped.sizeOptions
                      : scoped.partnerOptions
                  const setKey = option.value === 'land' ? setLandKey : option.value === 'size' ? setSizeKey : setPartnerKey
                  return (
                    <div key={option.value}>
                      <button
                        type="button"
                        onClick={() => setScope(option.value)}
                        className={`w-full ${filterButtonClass} ${
                          active
                            ? SELECTED_CLASS
                            : UNSELECTED_CLASS
                        }`}
                      >
                        {option.label}
                      </button>
                      {active && needsKey && mode === 'explorer' ? (
                        <select
                          value={keyValue}
                          onChange={(event) => setKey(event.target.value)}
                          className={selectClass}
                        >
                          {keyOptions.length === 0 ? <option value="">Нет данных</option> : null}
                          {keyOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      ) : null}
                    </div>
                  )
                })}
            </div>
          </div>
        </div>
      </aside>

      {/* Таблица */}
      <div className="min-w-0 flex-1">
        <Card className={cardClass}>
          <div className={compact ? 'flex items-center gap-2' : 'flex items-center gap-2.5'}>
            {!compact ? (
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
                <Trophy className="h-4 w-4" />
              </span>
            ) : null}
            <div className="min-w-0">
              <h2 className={`${compact ? 'text-sm' : 'text-base'} font-semibold tracking-tight`}>{dataset.label}</h2>
              <div className={`${compact ? 'text-[11px]' : 'text-xs'} text-muted-foreground`}>
                {scopeSummary}
              </div>
            </div>
            <div className="ml-auto flex items-center gap-1.5">
              {rightHeaderAction}
              {metricDetailLink ? (
                <Link
                  to={metricDetailLink}
                  className={`${compact ? 'rounded-md px-2 py-0.5 text-[11px]' : 'rounded-lg px-2.5 py-1 text-xs'} inline-flex items-center gap-1 border border-border/70 bg-background/70 font-medium text-muted-foreground transition-colors hover:border-primary/35 hover:bg-muted hover:text-foreground`}
                >
                  Детали
                  <ArrowRight className={`${compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} flex-none`} />
                </Link>
              ) : null}
              <span className={`${compact ? 'rounded-md px-2 py-0.5 text-[11px]' : 'rounded-full px-3 py-1 text-xs'} bg-muted font-semibold text-muted-foreground ring-1 ring-border`}>
                мест: {rows.length}
              </span>
            </div>
          </div>

          {rows.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border px-4 py-12 text-center text-sm text-muted-foreground">
              Нет данных за выбранный диапазон
            </div>
          ) : (
            <div className={tableOuterClass}>
              <div className="overflow-x-auto">
                <table className={`w-full table-auto ${compact ? 'text-xs' : 'text-sm'}`}>
                  <thead className="border-b border-border bg-muted/60">
                    <tr>
                      <th className={`${compact ? 'w-12' : 'w-16'} ${thClass}`}>№</th>
                      <th className={thClass}>
                        {isFranchisee ? 'Франчайзи' : 'Студия'}
                      </th>
                      <th className={thClass}>
                        {isFranchisee ? 'Города' : 'Город'}
                      </th>
                      {!isFranchisee ? (
                        <th className={thClass}>Франчайзи</th>
                      ) : null}
                      {hasDetail ? (
                        <th className={`${compact ? 'w-20' : 'w-[104px]'} ${thClass}`}>{dataset.detailColumnLabel}</th>
                      ) : null}
                      <th className={`${compact ? 'w-36' : 'w-[176px]'} ${thClass} text-right`}>{valueHeader}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row, index) => {
                      const isCurrent = currentId != null && row.id === currentId
                      return (
                        <Fragment key={`${row.id}-${index}`}>
                          {row.isGap && index > 0 ? (
                            <tr>
                              <td colSpan={isFranchisee ? (hasDetail ? 5 : 4) : (hasDetail ? 6 : 5)} className="py-1 text-center text-xs tracking-widest text-muted-foreground/60">• • •</td>
                            </tr>
                          ) : null}
                          <tr
                            className={`border-b border-border/40 align-middle transition-colors last:border-b-0 hover:bg-muted/40 ${rowAccentTone(row.place, isCurrent)}`}
                          >
                            <td className={tdClass}>
                              <span className={`inline-flex ${compact ? 'h-5 min-w-8 px-1.5 text-[11px]' : 'h-6 min-w-[2.25rem] px-2 text-xs'} items-center justify-center whitespace-nowrap rounded-full font-bold tabular-nums ${placeTone(row.place)}`}>
                                {row.place_label}
                              </span>
                            </td>
                            <td className={tdClass}>
                              <div className="line-clamp-1 font-semibold leading-tight" title={row.primary_label}>{row.primary_label}</div>
                            </td>
                            <td className={`${tdClass} text-muted-foreground`}>
                              <div className="line-clamp-1 leading-tight" title={row.secondary_label || undefined}>{row.secondary_label || '—'}</div>
                            </td>
                            {!isFranchisee ? (
                              <td className={`${tdClass} text-muted-foreground`}>
                                <div className="line-clamp-1 leading-tight" title={row.tertiary_label || undefined}>{row.tertiary_label || '—'}</div>
                              </td>
                            ) : null}
                            {hasDetail ? (
                              <td className={`${compact ? 'w-20' : 'w-[104px]'} ${tdClass} text-muted-foreground`}>
                                <div className="whitespace-nowrap leading-snug">{row.detail_label || '—'}</div>
                              </td>
                            ) : null}
                            <td className={`${compact ? 'w-36' : 'w-[176px]'} ${tdClass}`}>
                              <div className={`flex flex-col items-end ${compact ? 'gap-0.5' : 'gap-1'}`}>
                                <span className="whitespace-nowrap font-semibold tabular-nums text-foreground">
                                  {row.display_value ?? formatRatingValue(dataset.valueKind, row.metric_value)}
                                </span>
                                {maxValue > 0 && row.metric_value > 0 ? (
                                  <span className={`${compact ? 'h-1 max-w-[116px]' : 'h-1.5 max-w-[140px]'} block w-full overflow-hidden rounded-full bg-muted`}>
                                    <span
                                      className={`block h-full rounded-full ${barTone(row.place)}`}
                                      style={{ width: `${Math.max(4, Math.round((row.metric_value / maxValue) * 100))}%` }}
                                    />
                                  </span>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        </Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
